(() => {
  'use strict';

  const PILLOW_VARIANT_ID = '45292883673287';
  const ENDPOINT = '/api/2026-04/graphql.json';
  const REQUEST_TIMEOUT_MS = 15000;
  const EXPECTED_DISCOUNT = 200;
  const EXPECTED_PILLOW_TOTAL = 400;
  const GENERIC_ERROR = 'حصلت مشكلة بسيطة في تطبيق العرض. جرّب تاني لو سمحت.';
  const CHECKOUT_ERROR = 'حصلت مشكلة بسيطة في تجهيز طلبك. جرّب تاني لو سمحت.';
  const UNAVAILABLE_ERROR = 'العرض غير متاح دلوقتي، لكن تقدر تكمل طلبك.';

  const CART_CREATE_MUTATION = [
    'mutation TopperPrecheckoutCartCreate($input: CartInput!) {',
    '  cartCreate(input: $input) {',
    '    cart {',
    '      id',
    '      checkoutUrl',
    '      totalQuantity',
    '      lines(first: 20) {',
    '        nodes {',
    '          id',
    '          quantity',
    '          merchandise { ... on ProductVariant { id availableForSale } }',
    '          attributes { key value }',
    '          cost { totalAmount { amount currencyCode } }',
    '          discountAllocations { discountedAmount { amount currencyCode } }',
    '        }',
    '      }',
    '    }',
    '    userErrors { field message }',
    '    warnings { code message }',
    '  }',
    '}'
  ].join('\n');

  class TopperCheckoutError extends Error {
    constructor(code, message) {
      super(message);
      this.name = 'TopperCheckoutError';
      this.code = code;
    }
  }

  function init() {
    const root = document.querySelector('[data-topper-precheckout-root]');
    if (!root || root.dataset.initialized === 'true') return;

    const productId = String(root.dataset.productId || '').trim();
    const productHandle = String(root.dataset.productHandle || '').trim();
    const offerContext = String(root.dataset.offerContext || '').trim();
    const upsellVariantId = String(root.dataset.upsellVariantId || '').trim();
    const upsellSource = String(root.dataset.upsellSource || '').trim();
    const expectedUpsellSource = offerContext === 'topper'
      ? 'topper-precheckout'
      : offerContext === 'floor_mattress'
        ? 'floor-mattress-precheckout'
        : offerContext === 'fur_duvet'
          ? 'fur-duvet-precheckout'
          : '';

    const productRoot = root.closest('fluff-main-product');
    const dialog = root.querySelector('[data-topper-precheckout-dialog]');
    const closeButtons = Array.from(root.querySelectorAll('[data-topper-precheckout-close]'));
    const acceptButton = root.querySelector('[data-topper-precheckout-accept]');
    const declineButton = root.querySelector('[data-topper-precheckout-decline]');
    const errorBox = root.querySelector('[data-topper-precheckout-error]');
    const triggers = productRoot ? Array.from(productRoot.querySelectorAll('[data-topper-precheckout-trigger]')) : [];
    const allowlist = new Set((root.dataset.variantAllowlist || '').split(',').filter(Boolean));

    if (!productRoot || !dialog || !acceptButton || !declineButton || !errorBox || triggers.length !== 2) return;
    if (!/^\d+$/.test(productId) || !productHandle || !expectedUpsellSource) return;
    if (!/^\d+$/.test(upsellVariantId) || upsellVariantId !== PILLOW_VARIANT_ID) return;
    if (!allowlist.size || allowlist.has(upsellVariantId) || upsellSource !== expectedUpsellSource) return;

    root.dataset.initialized = 'true';
    const state = {
      activeTrigger: null,
      busy: false,
      decisionEvent: null,
      pillowUnavailable: root.dataset.pillowUnavailable === 'true'
    };

    const publish = (name, selection) => {
      try {
        if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') return;
        const result = Shopify.analytics.publish(name, {
          product_id: productId,
          product_handle: productHandle,
          variant_id: selection.variantId,
          quantity: selection.quantity,
          upsell_variant_id: upsellVariantId,
          offer_id: root.dataset.offerId || '2-soft-pillows-400',
          offer_context: offerContext
        });
        if (result && typeof result.catch === 'function') result.catch(() => {});
      } catch (_) {
        // Analytics must never block checkout.
      }
    };

    const readSelection = () => {
      const variantInput = productRoot.querySelector('[data-fluff-buy-now-variant-id]');
      const quantityInput = productRoot.querySelector('input[name="quantity"][data-fluff-quantity]');
      const variantId = variantInput ? String(variantInput.value || '').trim() : '';
      const quantity = quantityInput ? Number(quantityInput.value) : NaN;

      if (!allowlist.has(variantId)) throw new TopperCheckoutError('INVALID_VARIANT', 'Invalid mattress variant');
      if (!Number.isInteger(quantity) || quantity < 1) throw new TopperCheckoutError('INVALID_QUANTITY', 'Invalid mattress quantity');
      return { variantId, quantity };
    };

    const showProductError = () => {
      const productMessage = productRoot.querySelector('[data-fluff-message]');
      if (productMessage) productMessage.textContent = 'اختار المقاس والكمية بشكل صحيح وجرب تاني.';
    };

    const clearError = () => {
      errorBox.hidden = true;
      errorBox.textContent = '';
    };

    const showError = (message) => {
      errorBox.textContent = message;
      errorBox.hidden = false;
    };

    const setBusy = (action) => {
      state.busy = Boolean(action);
      root.setAttribute('aria-busy', state.busy ? 'true' : 'false');
      [acceptButton, declineButton].forEach((button) => {
        button.disabled = state.busy;
        button.classList.toggle('is-loading', state.busy && button === action);
        const loading = button.querySelector('[data-action-loading]');
        if (loading) loading.hidden = !(state.busy && button === action);
      });
      closeButtons.forEach((button) => { button.disabled = state.busy; });
      if (!state.busy && state.pillowUnavailable) acceptButton.disabled = true;
    };

    const openModal = (trigger) => {
      let selection;
      try {
        selection = readSelection();
      } catch (_) {
        showProductError();
        return;
      }

      state.activeTrigger = trigger;
      state.decisionEvent = null;
      clearError();
      root.hidden = false;
      document.body.classList.add('topper-precheckout-open');
      triggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => root.classList.add('is-open'));
      dialog.focus({ preventScroll: true });
      publish('fluff:upsell_shown', selection);

      if (state.pillowUnavailable) {
        acceptButton.disabled = true;
        showError(UNAVAILABLE_ERROR);
      }
    };

    const closeModal = () => {
      if (state.busy || root.hidden) return;
      root.classList.remove('is-open');
      root.hidden = true;
      document.body.classList.remove('topper-precheckout-open');
      triggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      const restore = state.activeTrigger;
      state.activeTrigger = null;
      if (restore && typeof restore.focus === 'function') restore.focus({ preventScroll: true });
    };

    const validateCheckoutUrl = (value) => {
      if (typeof value !== 'string' || !value) throw new TopperCheckoutError('MISSING_CHECKOUT_URL', 'Missing checkout URL');
      const parsed = new URL(value, window.location.origin);
      const trustedHost = parsed.hostname === window.location.hostname || parsed.hostname.endsWith('.myshopify.com');
      if (parsed.protocol !== 'https:' || !trustedHost) throw new TopperCheckoutError('INVALID_CHECKOUT_URL', 'Invalid checkout URL');
      return parsed.href;
    };

    const aggregateCart = (cart, selection, accepted) => {
      const lines = cart && cart.lines && Array.isArray(cart.lines.nodes) ? cart.lines.nodes : null;
      if (!lines) throw new TopperCheckoutError('INVALID_CART', 'Missing cart lines');

      const expected = new Map([[selection.variantId, selection.quantity]]);
      if (accepted) expected.set(upsellVariantId, 1);

      const aggregates = new Map();
      let pillowDiscount = 0;
      let pillowCost = 0;
      let pillowAttributesValid = !accepted;

      for (const line of lines) {
        const gid = line && line.merchandise && line.merchandise.id;
        const variantId = typeof gid === 'string' ? gid.split('/').pop() : '';
        if (!expected.has(variantId)) throw new TopperCheckoutError('UNEXPECTED_LINE', 'Unexpected merchandise');
        if (line.merchandise.availableForSale !== true) {
          const code = variantId === upsellVariantId ? 'PILLOW_UNAVAILABLE' : 'MATTRESS_UNAVAILABLE';
          throw new TopperCheckoutError(code, 'Unavailable merchandise');
        }

        const quantity = Number(line.quantity);
        if (!Number.isInteger(quantity) || quantity < 1) throw new TopperCheckoutError('INVALID_LINE_QUANTITY', 'Invalid line quantity');
        aggregates.set(variantId, (aggregates.get(variantId) || 0) + quantity);

        if (variantId === upsellVariantId) {
          const attributes = new Map((line.attributes || []).map((item) => [item.key, item.value]));
          pillowAttributesValid =
            attributes.get('_upsell_source') === upsellSource &&
            attributes.get('_upsell_offer') === '2-soft-pillows-400' &&
            attributes.get('_upsell_accepted') === 'yes';

          const lineCurrency = line.cost && line.cost.totalAmount && line.cost.totalAmount.currencyCode;
          if (lineCurrency !== 'EGP') throw new TopperCheckoutError('INVALID_CURRENCY', 'Invalid pillow currency');
          pillowCost += Number(line.cost.totalAmount.amount || 0);

          for (const allocation of line.discountAllocations || []) {
            const money = allocation && allocation.discountedAmount;
            if (!money || money.currencyCode !== 'EGP') throw new TopperCheckoutError('INVALID_DISCOUNT_CURRENCY', 'Invalid discount currency');
            pillowDiscount += Number(money.amount || 0);
          }
        }
      }

      if (aggregates.size !== expected.size) throw new TopperCheckoutError('MISSING_LINE', 'Missing expected line');
      for (const [variantId, requestedQuantity] of expected) {
        if (aggregates.get(variantId) !== requestedQuantity) throw new TopperCheckoutError('QUANTITY_MISMATCH', 'Cart quantity mismatch');
      }

      const expectedTotal = selection.quantity + (accepted ? 1 : 0);
      if (Number(cart.totalQuantity) !== expectedTotal) throw new TopperCheckoutError('TOTAL_QUANTITY_MISMATCH', 'Cart total mismatch');

      if (accepted) {
        if (!pillowAttributesValid) throw new TopperCheckoutError('INVALID_ATTRIBUTES', 'Missing upsell attributes');
        if (Math.abs(pillowDiscount - EXPECTED_DISCOUNT) > 0.001 || Math.abs(pillowCost - EXPECTED_PILLOW_TOTAL) > 0.001) {
          throw new TopperCheckoutError('PRICING_MISMATCH', 'Upsell pricing mismatch');
        }
      }
    };

    const createIsolatedCart = async (selection, accepted) => {
      const lines = [{
        merchandiseId: 'gid://shopify/ProductVariant/' + selection.variantId,
        quantity: selection.quantity
      }];

      if (accepted) {
        lines.push({
          merchandiseId: 'gid://shopify/ProductVariant/' + upsellVariantId,
          quantity: 1,
          attributes: [
            { key: '_upsell_source', value: upsellSource },
            { key: '_upsell_offer', value: '2-soft-pillows-400' },
            { key: '_upsell_accepted', value: 'yes' }
          ]
        });
      }

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      let response;

      try {
        response = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query: CART_CREATE_MUTATION, variables: { input: { lines } } }),
          signal: controller.signal,
          credentials: 'same-origin'
        });
      } catch (error) {
        const code = error && error.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK';
        throw new TopperCheckoutError(code, 'Storefront request failed');
      } finally {
        window.clearTimeout(timeout);
      }

      if (!response.ok) throw new TopperCheckoutError('HTTP', 'Storefront response failed');

      let payload;
      try {
        payload = await response.json();
      } catch (_) {
        throw new TopperCheckoutError('INVALID_JSON', 'Invalid Storefront response');
      }

      if (Array.isArray(payload.errors) && payload.errors.length) throw new TopperCheckoutError('GRAPHQL', 'GraphQL error');
      const result = payload && payload.data && payload.data.cartCreate;
      if (!result) throw new TopperCheckoutError('MISSING_RESULT', 'Missing cartCreate result');
      if (Array.isArray(result.userErrors) && result.userErrors.length) throw new TopperCheckoutError('CART_USER_ERROR', result.userErrors[0].message || 'Cart error');
      if (Array.isArray(result.warnings) && result.warnings.length) throw new TopperCheckoutError('CART_WARNING', result.warnings[0].message || 'Cart warning');
      if (!result.cart) throw new TopperCheckoutError('MISSING_CART', 'Missing cart');

      aggregateCart(result.cart, selection, accepted);
      return validateCheckoutUrl(result.cart.checkoutUrl);
    };

    const processDecision = async (accepted, button) => {
      if (state.busy) return;

      let selection;
      try {
        selection = readSelection();
      } catch (_) {
        showError(CHECKOUT_ERROR);
        return;
      }

      clearError();
      setBusy(button);
      const eventName = accepted ? 'fluff:upsell_accepted' : 'fluff:upsell_declined';
      if (state.decisionEvent !== eventName) {
        state.decisionEvent = eventName;
        publish(eventName, selection);
      }

      try {
        const checkoutUrl = await createIsolatedCart(selection, accepted);
        window.location.assign(checkoutUrl);
      } catch (error) {
        setBusy(null);
        const unavailable = error && (error.code === 'PILLOW_UNAVAILABLE' || error.code === 'MATTRESS_UNAVAILABLE');
        if (error && error.code === 'PILLOW_UNAVAILABLE') state.pillowUnavailable = true;
        showError(unavailable ? UNAVAILABLE_ERROR : (accepted ? GENERIC_ERROR : CHECKOUT_ERROR));
      }
    };

    triggers.forEach((trigger) => {
      trigger.hidden = false;
      trigger.addEventListener('click', () => openModal(trigger));
    });
    closeButtons.forEach((button) => button.addEventListener('click', closeModal));
    acceptButton.addEventListener('click', () => processDecision(true, acceptButton));
    declineButton.addEventListener('click', () => processDecision(false, declineButton));

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== 'Tab' || state.busy) return;

      const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]):not([hidden]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();